// Mock PostJson für den Renderer-Smoke-Test auf /s/infografik-port-test.
//
// Port 1 (Choropleth, 2026-07-20): fährt einen `infographic`-Layer durch den
// echten Renderer — Dispatch (updateLayer) → updateInfographic →
// buildInfographicSvg → DOM-Overlay → Positionierung (renderLayerHTML).
// Content ist eine gekürzte Fassung aus Jans Editor-Export
// (`scrolly-new-2026-07-20_infografik.json`, chartType `mapEurope`).
//
// Klein halten — Smoke-Test-Fixture, keine Produktionsdatenquelle
// (gleiche Konvention wie hamburgsMietpreise.ts).
import type { PostJson } from "@scrolly/post-types";

const ANIM = {
  scale: { id: "s", type: "scale", active: false, keyframes: [], initialValue: [1, 1, 1] },
  opacity: { id: "o", type: "opacity", active: false, keyframes: [], initialValue: 1 },
  position: { id: "p", type: "position", active: false, keyframes: [], initialValue: [0, 0, 0] },
  rotation: { id: "r", type: "rotation", active: false, keyframes: [], initialValue: [0, 0, 0] },
};

// Reveal-Kanal: die Karte färbt sich über die Lebenszeit des Layers ein.
// Nur der infographic-Layer bekommt ihn — die Stage-Gruppe bleibt bei den
// vier Legacy-Kanälen (so wie Alt-Scrollys aussehen).
const ANIM_WITH_PROGRESS = {
  ...ANIM,
  progress: {
    id: "prog",
    type: "progress",
    active: true,
    initialValue: 0,
    keyframes: [
      { id: "k0", start: 0, value: 0, easing: "quadInOut" },
      { id: "k1", start: 1500, value: 1, easing: "quadInOut" },
    ],
  },
};

export const mockPostJsonInfografikPortTest: PostJson = {
  data: {
    id: "dev-seed-infografik-port-test",
    data: {
      id: "new",
      groups: [
        {
          id: "layer_group_1",
          to: 2000,
          from: 0,
          name: "Stage",
          type: "group",
          content: { layerIds: ["infographic-1"] },
          visible: true,
          pivotPoint: [0, 0, 0],
          animationsGroups: ANIM,
        },
      ],
      layers: [
        {
          id: "infographic-1",
          to: 2000,
          from: 0,
          name: "Infographic",
          type: "infographic",
          visible: true,
          pivotPoint: [0, 0, 0],
          animationsGroups: ANIM_WITH_PROGRESS,
          content: {
            layerIds: [],
            name: "Infographic",
            chartType: "mapEurope",
            width: 930,
            height: 1200,
            title: "A tiny story in data",
            source: "IEA, OECD and IMF via UN Global SDG Database — 2021",
            data: [
              { label: "Austria", value: 108.81 },
              { label: "Belgium", value: 323.03 },
              { label: "Bulgaria", value: 353.33 },
              { label: "Denmark", value: 259 },
              { label: "Finland", value: 272.82 },
              { label: "France", value: 154.14 },
              { label: "Germany", value: 94.27 },
              { label: "Greece", value: 203.41 },
              { label: "Ireland", value: 497.42 },
              { label: "Italy", value: 209.6 },
              { label: "Poland", value: 30.22 },
              { label: "Spain", value: 35.46 },
              { label: "Sweden", value: 255.82 },
            ],
            palette: ["#96C81E", "#FFC800", "#FF9600", "#F02873", "#1996D0"],
            // Dunkler Text auf hellem Kartenhintergrund: macht das Fixture auf
            // JEDEM Untergrund sichtbar (Jans Original stand auf einem dunklen
            // Hintergrundbild und nutzte weiße Schrift auf transparent).
            textColor: "#1a1a1a",
            backgroundColor: { r: 255, g: 255, b: 255, a: 0.92 },
            accentColor: "#6c8cff",
            showLabels: true,
            showValues: true,
            mapColorMode: "value",
            mapColorRamp: "orRd",
            mapThresholds: [10, 20, 50, 100, 200, 500, 1000],
            mapLegend: true,
            mapNoDataLabel: "No data",
            titleFontSize: 98,
            sourceFontSize: 33,
            mapLegendFontSize: 31,
            fontFamily: "BarlowSemiCondensed",
            valuePrefix: "$",
          },
        },
      ],
      slides: [
        {
          id: "slide-1",
          from: 0,
          name: "Step 1",
          text: { show: true, text: "Choropleth aus Jans Pro-Editor — Port 1 Smoke-Test." },
          credits: { text: "", light: false },
          options: null,
          isChapter: true,
        },
        {
          // Zweiter Slide: ohne ihn steht der Zeitgeber fest auf 0. Der Reader
          // bildet den Scroll auf die Spanne [vorige Slide.from, Slide.from] ab;
          // bei nur einer Slide ist das [0, 0]. Der progress-Kanal oben steht
          // bei t=0 auf 0, alle 45 Laenderflaechen blieben unsichtbar
          // (Befund 04.10.2026, gemessen auf dev-viewer: timer=0, progress=0).
          // Gleicher Aufbau wie app/src/pages/dev-fixtures/infografikPortTest.ts.
          id: "slide-2",
          from: 2000,
          name: "Step 2",
          text: { show: true, text: "Zweiter Beat: die Karte ist fertig aufgebaut." },
          credits: { text: "", light: false },
          options: null,
          isChapter: false,
        },
      ],
      presentationOptions: {
        responsive: true,
        slideOptions: {
          margin: 12,
          padding: 16,
          scrollable: false,
          borderRadius: 12,
          animationType: "opacity",
          alignAtSafeArea: true,
          companyBoxPosition: "left",
          safeAreaXPosition: 0,
          safeAreaYPosition: 0,
        },
        ambientIntensity: 1,
      },
    },
    slug: "infografik-port-test",
    title: "Infografik Port-Test",
    authors: [],
    tenants: ["scrlly"],
    metadata: {
      type: "visual",
      theme: "light",
      title: "Infografik Port-Test",
      hidden: false,
      subtitle: "Choropleth-Smoke-Test",
      description: "Port-1 Renderer-Smoke-Test",
      hideIntroOverlay: false,
    },
    visibility: "public",
    publishState: "published",
  },
} as unknown as PostJson;
